/**
 * Formatea un objeto Date de JavaScript al formato "DD-MM-YYYY" que requiere la API de Mindicador.
 * @param date El objeto Date a formatear.
 * @returns La fecha como un string.
 */
function formatDateForApi(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Se suma 1 porque los meses van de 0 a 11
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Función genérica para obtener el valor de cualquier indicador desde la API.
 * @param indicator El nombre del indicador (ej: 'uf', 'dolar', 'euro', 'utm').
 * @param date La fecha para la cual se quiere obtener el valor.
 * @returns El valor del indicador como un número, o null si ocurre un error.
 */
async function getIndicatorValue(
  indicator: string,
  date: Date
): Promise<number | null> {
  const formattedDate = formatDateForApi(date);
  const apiUrl = `https://mindicador.cl/api/${indicator}/${formattedDate}`;

  try {
    console.log(`Consultando API de ${indicator.toUpperCase()}: ${apiUrl}`);
    const response = await fetch(apiUrl);

    if (!response.ok) {
      console.error(
        `Error al obtener el valor de ${indicator.toUpperCase()}: La respuesta de la red no fue exitosa.`
      );
      return null;
    }

    const data = await response.json();

    if (data.serie && data.serie.length > 0 && data.serie[0].valor) {
      return data.serie[0].valor;
    } else {
      console.error(
        `Error al obtener el valor de ${indicator.toUpperCase()}: El formato de los datos no es válido o no hay valor para esa fecha.`,
        data
      );
      return null;
    }
  } catch (error) {
    console.error(
      `Ocurrió un error al intentar obtener el valor de ${indicator.toUpperCase()}:`,
      error
    );
    return null;
  }
}

export const indicadorService = {
  /**
   * Obtiene el valor de la UF para una fecha específica.
   */
  getUfValue(date: Date): Promise<number | null> {
    return getIndicatorValue("uf", date);
  },

  /**
   * Obtiene el valor del Dólar Observado para una fecha específica.
   */
  getDolarValue(date: Date): Promise<number | null> {
    return getIndicatorValue("dolar", date);
  },

  /**
   * Obtiene el valor del Euro para una fecha específica.
   */
  getEuroValue(date: Date): Promise<number | null> {
    return getIndicatorValue("euro", date);
  },

  /**
   * Obtiene el valor de la UTM (Unidad Tributaria Mensual) para una fecha específica.
   */
  getUtmValue(date: Date): Promise<number | null> {
    return getIndicatorValue("utm", date);
  },

  /**
   * Obtiene el valor del IPC (Índice de Precios al Consumidor) para una fecha específica.
   */
  getIpcValue(date: Date): Promise<number | null> {
    return getIndicatorValue("ipc", date);
  },
};
